import React, { FunctionComponent, useMemo } from "react";

import { ContentSubtype } from "../common/types/ContentSubtype";
import { MovieContentSubtype } from "../common/types/MovieContentSubtype";
import { ProtectedRoute, RouteItem } from "../core/routing/ProtectedRoute";
import { SprintAccessRight } from "../core/security/SprintAccessRight";
import { AncillaryDashboard } from "./ancillary/listing/AncillaryDashboard";
import { MovieDashboard } from "./movie/details/MovieDashboard";
import { MovieListing } from "./movie/listing/MovieListing";
import { TvSeriesListing } from "./tv-series/listing/TvSeriesListing";
import { TvSeriesDashboard } from "./tv-series/TvSeriesDashboard";

export const contentBasePath = "/content";

const manageAssetsMinAccess = [SprintAccessRight.AssetsReadWrite];

export const assetsManageMovieListingRoute: RouteItem = {
    path: `${contentBasePath}/movies`,
    component: () => MovieListing({ contentType: ContentSubtype.Movie }),
    accessRights: manageAssetsMinAccess,
    name: "Movies"
};

export const assetsManageTvSingleListingRoute: RouteItem = {
    path: `${contentBasePath}/tv-singles`,
    component: () => MovieListing({ contentType: ContentSubtype.TvSingle }),
    accessRights: manageAssetsMinAccess,
    name: "TV Singles"
};

export const assetsManageDocumentaryListingRoute: RouteItem = {
    path: `${contentBasePath}/feature-documentaries`,
    component: () => MovieListing({ contentType: ContentSubtype.FeatureDocumentary }),
    accessRights: manageAssetsMinAccess,
    name: "Feature Documentaries"
};

export const assetsManageShortFilmsListingRoute: RouteItem = {
    path: `${contentBasePath}/short-films`,
    component: () => MovieListing({ contentType: ContentSubtype.ShortFilm }),
    accessRights: manageAssetsMinAccess,
    name: "Short Films"
};

export const assetsManageTvSingleRoute: RouteItem = {
    path: `${contentBasePath}/tv-singles/:movieId`,
    component: () => MovieDashboard({ contentType: ContentSubtype.TvSingle }),
    accessRights: manageAssetsMinAccess,
    name: "Tv Single Title"
};

export const assetsManageMovieRoute: RouteItem = {
    path: `${contentBasePath}/movies/:movieId`,
    component: () => MovieDashboard({ contentType: ContentSubtype.Movie }),
    accessRights: manageAssetsMinAccess,
    name: "Movie Title"
};

export const assetsManageDocumentaryRoute: RouteItem = {
    path: `${contentBasePath}/feature-documentaries/:movieId`,
    component: () => MovieDashboard({ contentType: ContentSubtype.FeatureDocumentary }),
    accessRights: manageAssetsMinAccess,
    name: "Feature Documentary Title"
};

export const assetsManageShortFilmsRoute: RouteItem = {
    path: `${contentBasePath}/short-films/:movieId`,
    component: () => MovieDashboard({ contentType: ContentSubtype.ShortFilm }),
    accessRights: manageAssetsMinAccess,
    name: "Short Film Title"
};

export const assetsManageTvSeriesRoute: RouteItem = {
    path: `${contentBasePath}/tv-series/:tvSeriesId`,
    component: TvSeriesDashboard,
    accessRights: manageAssetsMinAccess,
    name: "Tv Series Title"
};

export const assetsManageTvSeriesListingRoute: RouteItem = {
    path: `${contentBasePath}/tv-series`,
    component: TvSeriesListing,
    accessRights: manageAssetsMinAccess,
    name: "TV Series"
};

export const assetsManageAncillaryPromoListingRoute: RouteItem = {
    path: `${contentBasePath}/promos`,
    component: () => AncillaryDashboard({ contentType: ContentSubtype.Promo }),
    accessRights: manageAssetsMinAccess,
    name: "Promos"
};
export const assetsManageAncillarySafetyListingRoute: RouteItem = {
    path: `${contentBasePath}/safeties`,
    component: () => AncillaryDashboard({ contentType: ContentSubtype.Safety }),
    accessRights: manageAssetsMinAccess,
    name: "Safeties"
};
export const assetsManageAncillaryAdvertListingRoute: RouteItem = {
    path: `${contentBasePath}/adverts`,
    component: () => AncillaryDashboard({ contentType: ContentSubtype.Advert }),
    accessRights: manageAssetsMinAccess,
    name: "Adverts"
};
export const assetsManageAncillaryIndentWarningListingRoute: RouteItem = {
    path: `${contentBasePath}/indentwarnings`,
    component: () => AncillaryDashboard({ contentType: ContentSubtype.IndentWarning }),
    accessRights: manageAssetsMinAccess,
    name: "Indent Warnings"
};

export const contentManageRoutes = {
    routeItems: [
        assetsManageMovieListingRoute,
        assetsManageTvSingleListingRoute,
        assetsManageDocumentaryListingRoute,
        assetsManageShortFilmsListingRoute,
        assetsManageTvSeriesListingRoute,
        assetsManageAncillaryPromoListingRoute,
        assetsManageAncillarySafetyListingRoute,
        assetsManageAncillaryAdvertListingRoute,
        assetsManageAncillaryIndentWarningListingRoute,
        assetsManageMovieRoute,
        assetsManageTvSingleRoute,
        assetsManageDocumentaryRoute,
        assetsManageShortFilmsRoute,
        assetsManageTvSeriesRoute
    ],
    baseRouteNames: ["Content"]
};

const assetsRoutes = [...contentManageRoutes.routeItems];

export const ContentRoutes: FunctionComponent = () => {
    const routes = useMemo(
        () =>
            assetsRoutes.map((routeItem: RouteItem) => (
                <ProtectedRoute key={routeItem.path} protectedRouteData={routeItem} />
            )),
        [assetsRoutes]
    );

    return <div>{routes}</div>;
};

export const getMovieDashboardHref = (movieId: string, contentType: MovieContentSubtype): string => {
    const manageAssetsPath: Record<MovieContentSubtype, string> = {
        [ContentSubtype.Movie]: assetsManageMovieRoute.path,
        [ContentSubtype.TvSingle]: assetsManageTvSingleRoute.path,
        [ContentSubtype.FeatureDocumentary]: assetsManageDocumentaryRoute.path,
        [ContentSubtype.ShortFilm]: assetsManageShortFilmsRoute.path
    };

    const path = manageAssetsPath[contentType];

    return path.replace(":movieId", movieId);
};

export const getTvSeriesDashboardHref = (tvSeriesId: string): string => {
    return assetsManageTvSeriesRoute.path.replace(":tvSeriesId", tvSeriesId);
};
